var _a;
import { __assign } from "tslib";
/**
 * The MIT License (MIT)
 * https://github.com/aurelia/dependency-injection
 *
 * Copyright (c) 2010 - 2018 Blue Spire Inc.
 */
import { construct, getMetadata, getOwnMetadata, hasOwnMetadata } from '../localReflect';
import { AggregateError } from '../AggregateError';
import constants from '../constants';
import { containerResolver as resolverDeco } from '../protocol/resolver';
import { Strategy, StrategyResolver } from '../resolvers/StrategyResolver';
import { isStrategyResolver, } from '../types';
import { getInjectDependencies } from './getInjectDependencies';
import { InvocationHandler } from './InvocationHandler';
import { _emptyParameters, validateKey } from './validateParameters';
// tslint:disable:max-line-length
function invokeWithDynamicDependencies(container, fn, staticDependencies, dynamicDependencies) {
    var i = staticDependencies.length;
    var args = new Array(i);
    var lookup;
    while (i--) {
        lookup = staticDependencies[i];
        switch (lookup) {
            case null:
            case undefined:
                throw new Error("Constructor " + fn.name + " Parameter with index " + i + " cannot be null or undefined. Are you trying to inject/register something that doesn't exist with DI?");
            default:
                args[i] = container.get(lookup);
        }
    }
    if (dynamicDependencies !== undefined) {
        args = args.concat(dynamicDependencies);
    }
    return construct(fn, args);
}
// tslint:disable:no-magic-numbers
var classInvokers = (_a = {},
    _a[0] = {
        invoke: function (container, type, deps) {
            return new type();
        },
        invokeWithDynamicDependencies: invokeWithDynamicDependencies,
    },
    _a[1] = {
        invoke: function (container, type, deps) {
            return new type(container.get(deps[0]));
        },
        invokeWithDynamicDependencies: invokeWithDynamicDependencies,
    },
    _a[2] = {
        invoke: function (container, type, deps) {
            return new type(container.get(deps[0]), container.get(deps[1]));
        },
        invokeWithDynamicDependencies: invokeWithDynamicDependencies,
    },
    _a[3] = {
        invoke: function (container, type, deps) {
            return new type(container.get(deps[0]), container.get(deps[1]), container.get(deps[2]));
        },
        invokeWithDynamicDependencies: invokeWithDynamicDependencies,
    },
    _a[4] = {
        invoke: function (container, type, deps) {
            return new type(container.get(deps[0]), container.get(deps[1]), container.get(deps[2]), container.get(deps[3]));
        },
        invokeWithDynamicDependencies: invokeWithDynamicDependencies,
    },
    _a[5] = {
        invoke: function (container, type, deps) {
            return new type(container.get(deps[0]), container.get(deps[1]), container.get(deps[2]), container.get(deps[3]), container.get(deps[4]));
        },
        invokeWithDynamicDependencies: invokeWithDynamicDependencies,
    },
    _a.length = 6,
    _a.fallback = {
        invoke: invokeWithDynamicDependencies,
        invokeWithDynamicDependencies: invokeWithDynamicDependencies,
    },
    _a);
// tslint:enable:no-magic-numbers
var containerNumber = 0;
/**
 * A lightweight, extensible dependency injection container.
 */
var Container = /** @class */ (function () {
    /**
     * Creates an instance of Container.
     * @param configuration Provides some configuration for the new Container instance.
     */
    function Container(configuration) {
        this._metadata = new WeakMap();
        if (configuration === undefined) {
            // tslint:disable-next-line:no-parameter-reassignment
            configuration = { name: 'root' };
        }
        containerNumber++;
        this._configuration = configuration;
        this._onHandlerCreated = configuration.onHandlerCreated;
        this._onRegisterResolver = configuration.onRegisterResolver;
        this._handlers = configuration.handlers || (configuration.handlers = new Map());
        this._resolvers = new Map();
        this.root = this;
        this.parent = undefined;
    }
    Object.defineProperty(Container.prototype, "name", {
        get: function () {
            return this._configuration.name;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Makes this container instance globally reachable through Container.instance.
     */
    Container.prototype.makeGlobal = function () {
        Container.instance = this;
        return this;
    };
    /**
     * Sets an invocation handler creation callback that will be called when new InvocationsHandlers
     *  are created (called once per Function).
     * @param onHandlerCreated The callback to be called when an InvocationsHandler is created.
     */
    Container.prototype.setHandlerCreatedCallback = function (onHandlerCreated) {
        this._onHandlerCreated = onHandlerCreated;
        this._configuration.onHandlerCreated = onHandlerCreated;
    };
    /**
     * @param onRegisterResolver The callback to be called when a Resolver is created.
     */
    Container.prototype.setRegisterResolver = function (onRegisterResolver) {
        this._onRegisterResolver = onRegisterResolver;
        this._configuration.onRegisterResolver = onRegisterResolver;
    };
    /**
     * Registers an existing object instance with the container.
     * @param key The key that identifies the dependency at resolution time; usually a constructor function.
     * @param instance The instance that will be resolved when the key is matched. This defaults to the key
     *                  value when instance is not supplied.
     * @return The resolver that was registered.
     */
    Container.prototype.registerInstance = function (key, instance) {
        return this.registerResolver(key, new StrategyResolver(Strategy.Instance, instance === undefined ? key : instance));
    };
    Container.prototype.registerSingleton = function (key, fn) {
        return this.registerResolver(key, new StrategyResolver(Strategy.Singleton, fn === undefined ? key : fn));
    };
    Container.prototype.registerScoped = function (key, fn) {
        return this.registerResolver(key, new StrategyResolver(Strategy.Scoped, fn === undefined ? key : fn));
    };
    Container.prototype.registerTransient = function (key, fn) {
        return this.registerResolver(key, new StrategyResolver(Strategy.Transient, fn === undefined ? key : fn));
    };
    /**
     * Registers a custom resolution function such that the container calls this function for each request
     *      to obtain the instance.
     * @param key The key that identifies the dependency at resolution time; usually a constructor function.
     * @param handler The resolution function to use when the dependency is needed.
     * @return The resolver that was registered.
     */
    Container.prototype.registerHandler = function (key, handler) {
        return this.registerResolver(key, new StrategyResolver(Strategy.Function, handler));
    };
    /**
     * Registers an additional key that serves as an alias to the original DI key.
     * @param originalKey The key that originally identified the dependency; usually a constructor function.
     * @param aliasKey An alternate key which can also be used to resolve the same dependency  as the original.
     * @return The resolver that was registered.
     */
    Container.prototype.registerAlias = function (originalKey, aliasKey) {
        return this.registerResolver(aliasKey, new StrategyResolver(Strategy.Alias, originalKey));
    };
    /**
     * Registers a custom resolution function such that the container calls this function for each request
     *              to obtain the instance.
     * @param key The key that identifies the dependency at resolution time; usually a constructor function.
     * @param resolver The resolver to use when the dependency is needed.
     * @return The resolver that was registered.
     */
    Container.prototype.registerResolver = function (key, resolver) {
        validateKey(key);
        resolver =
            this._onRegisterResolver != null ? this._onRegisterResolver(key, resolver) : resolver;
        var allResolvers = this._resolvers;
        var result = allResolvers.get(key);
        if (result === undefined) {
            allResolvers.set(key, resolver);
        }
        else if (result instanceof StrategyResolver && result.strategy === Strategy.Array) {
            result.state.unshift(resolver);
        }
        else {
            allResolvers.set(key, new StrategyResolver(Strategy.Array, [resolver, result]));
        }
        return resolver;
    };
    Container.prototype.autoRegister = function (key, fn) {
        // tslint:disable-next-line:no-parameter-reassignment
        fn = fn === undefined ? key : fn;
        if (typeof fn === 'function') {
            var registration = getMetadata(constants.registration, fn);
            if (registration === undefined) {
                return this.registerResolver(key, new StrategyResolver(Strategy.Singleton, fn));
            }
            return registration.registerResolver(this, key, fn);
        }
        throw new Error("Cannot auto register a non method for key " + key.toString());
    };
    /**
     * Registers an array of types (constructor functions) by inspecting their registration annotations.
     *      If none are found, then the default singleton registration is used.
     * @param fns The constructor function to use when the dependency needs to be instantiated.
     */
    Container.prototype.autoRegisterAll = function (fns) {
        var i = fns.length;
        while (i--) {
            this.autoRegister(fns[i]);
        }
    };
    /**
     * Unregisters based on key.
     * @param key The key that identifies the dependency at resolution time; usually a constructor function.
     */
    Container.prototype.unregister = function (key) {
        this._resolvers.delete(key);
    };
    /**
     * Inspects the container to determine if a particular key has been registred.
     * @param key The key that identifies the dependency at resolution time; usually a constructor function.
     * @param checkParent Indicates whether or not to check the parent container hierarchy.
     * @return Returns true if the key has been registred; false otherwise.
     */
    Container.prototype.hasHandler = function (key, checkParent) {
        if (checkParent === void 0) { checkParent = false; }
        validateKey(key);
        return (!!this.getResolver(key) ||
            (checkParent && this.parent != null && this.parent.hasHandler(key, checkParent)));
    };
    /**
     * Gets the resolver for the particular key, if it has been registered.
     * @param key The key that identifies the dependency at resolution time; usually a constructor function.
     * @return Returns the resolver, if registred, otherwise undefined.
     */
    Container.prototype.getResolver = function (key, checkParent) {
        if (checkParent === void 0) { checkParent = false; }
        return this._getResolver(key, checkParent ? this : undefined);
    };
    /**
     * Resolves a single instance based on the provided key.
     * @param key The key that identifies the object to resolve.
     * @return Returns the resolved instance.
     */
    Container.prototype.get = function (key) {
        validateKey(key);
        if (key === Container) {
            return this;
        }
        if (resolverDeco.decorates(key)) {
            return key.get(this, key);
        }
        var resolver = this.getResolver(key, true);
        if (resolver === undefined) {
            if (this.parent == null) {
                return this.autoRegister(key).get(this, key);
            }
            if (typeof key !== 'symbol' && typeof key !== 'string') {
                var registration = getMetadata(constants.registration, key);
                if (registration) {
                    return registration.registerResolver(this, key, key).get(this, key);
                }
            }
            return this.parent._get(key, this);
        }
        return resolver.get(this, key);
    };
    /**
     * Resolves all instance registered under the provided key.
     * @param key The key that identifies the objects to resolve.
     * @return Returns an array of the resolved instances.
     */
    Container.prototype.getAll = function (key) {
        validateKey(key);
        var resolver = this.getResolver(key, true);
        if (resolver === undefined) {
            if (this.parent == null) {
                return _emptyParameters;
            }
            return this.parent.getAll(key);
        }
        var results;
        if (resolver instanceof StrategyResolver && resolver.strategy === Strategy.Array) {
            var state = resolver.state;
            var len = state.length;
            var i = len;
            results = new Array(i);
            while (i--) {
                results[len - i - 1] = state[i].get(this, key);
            }
        }
        else {
            results = [resolver.get(this, key)];
        }
        return this.parent ? this.parent.getAll(key).concat(results) : results;
    };
    /**
     * Creates a new dependency injection container whose parent is the current container.
     * @return Returns a new container instance parented to this.
     */
    Container.prototype.createChild = function (name) {
        var child = new Container(__assign(__assign({}, this._configuration), { name: (name || 'child') + ":" + containerNumber }));
        child.root = this.root;
        child.parent = this;
        return child;
    };
    /**
     * Configure the container with a given delegate method
     */
    Container.prototype.configure = function (func) {
        func(this);
        return this;
    };
    /**
     * Invokes a function, recursively resolving its dependencies.
     * @param fn The function to invoke with the auto-resolved dependencies.
     * @param dynamicDependencies Additional function dependencies to use during invocation.
     * @return Returns the instance resulting from calling the function.
     */
    Container.prototype.invoke = function (fn, dynamicDependencies) {
        try {
            var handler = this._handlers.get(fn);
            if (handler === undefined) {
                handler = this._createInvocationHandler(fn);
                this._handlers.set(fn, handler);
            }
            return handler.invoke(this, dynamicDependencies);
        }
        catch (e) {
            throw AggregateError("Error invoking " + fn.name + ". Check the inner error for details.", e, true);
        }
    };
    /**
     * Invokes a function, recursively resolving its dependencies.
     * @param fn The function to invoke with the auto-resolved dependencies.
     * @param key The key being invoked.
     * @param dynamicDependencies Additional function dependencies to use during invocation.
     * @return Returns the instance resulting from calling the function.
     */
    Container.prototype.invokeWithKey = function (fn, key, dynamicDependencies) {
        var value = typeof fn !== 'function' ? fn.get(this, key) : this.invoke(fn, dynamicDependencies);
        if (typeof key !== 'symbol' &&
            typeof key !== 'string' &&
            hasOwnMetadata(constants.wrap, key)) {
            var wrap = getOwnMetadata(constants.wrap, key);
            return wrap.get(value, this, key);
        }
        return value;
    };
    /**
     * Disposes of this container
     */
    Container.prototype.dispose = function () {
        var resolvers = this._resolvers;
        this._resolvers.clear();
        this._resolvers = null;
        this._configuration = null;
        this.parent = null;
        this.root = null;
    };
    Container.prototype._getResolver = function (key, relativeContainer) {
        var resolver = this._resolvers.get(key);
        if (relativeContainer && !resolver && this.parent) {
            resolver = this.parent._getResolver(key, relativeContainer);
        }
        if (!this._resolvers.get(key) &&
            isStrategyResolver(resolver) &&
            resolver.strategy === Strategy.Scoped) {
            resolver = resolver.clone();
            this.registerResolver(key, resolver);
        }
        return resolver;
    };
    Container.prototype._get = function (key, relativeContainer) {
        var resolver = this.getResolver(key, true);
        if (resolver === undefined) {
            if (this.parent == null) {
                return (relativeContainer || this)
                    .autoRegister(key)
                    .get(relativeContainer || this, key);
            }
            return this.parent._get(key, relativeContainer || this);
        }
        return resolver.get(relativeContainer || this, key);
    };
    Container.prototype._createInvocationHandler = function (fn) {
        var dependencies = getInjectDependencies(fn);
        var invoker = getOwnMetadata(constants.invoker, fn) ||
            classInvokers[dependencies.length] ||
            classInvokers.fallback;
        var handler = new InvocationHandler(fn, invoker, dependencies);
        return this._onHandlerCreated != null ? this._onHandlerCreated(handler) : handler;
    };
    Container.prototype._getOrCreateMetadataMap = function (O, P, Create) {
        var targetMetadata = this._metadata.get(O);
        if (targetMetadata === undefined) {
            if (!Create)
                return undefined;
            targetMetadata = new Map();
            this._metadata.set(O, targetMetadata);
        }
        var metadataMap = targetMetadata.get(P);
        if (metadataMap === undefined) {
            if (!Create)
                return undefined;
            metadataMap = new Map();
            targetMetadata.set(P, metadataMap);
        }
        return metadataMap;
    };
    /**
      * Gets a value indicating whether the target object has the provided metadata key defined.
      * @param metadataKey A key used to store and retrieve metadata.
      * @param target The target object on which the metadata is defined.
      * @param propertyKey The property key for the target.
      * @returns `true` if the metadata key was defined on the target object; otherwise, `false`.
      * @internal
      * @example
      *
      *     class Example {
      *         // property declarations are not part of ES6, though they are valid in TypeScript:
      *         // static staticProperty;
      *         // property;
      *
      *         static staticMethod(p) { }
      *         method(p) { }
      *     }
      *
      *     // property (on constructor)
      *     result = hasOwnMetadata("custom:annotation", Example, "staticProperty");
      *
      *     // property (on prototype)
      *     result = hasOwnMetadata("custom:annotation", Example.prototype, "property");
      *
      *     // method (on constructor)
      *     result = hasOwnMetadata("custom:annotation", Example, "staticMethod");
      *
      *     // method (on prototype)
      *     result = hasOwnMetadata("custom:annotation", Example.prototype, "method");
      *
      */
    Container.prototype.hasOwnMetadata = function (metadataKey, target, propertyKey) {
        var metadataMap = this._getOrCreateMetadataMap(target, propertyKey, /*Create*/ false);
        if (metadataMap === undefined)
            return false;
        return metadataMap.has(metadataKey);
    };
    /**
      * Define a unique metadata entry on the target.
      * @param metadataKey A key used to store and retrieve metadata.
      * @param metadataValue A value that contains attached metadata.
      * @param target The target object on which to define metadata.
      * @param propertyKey The property key for the target.
      * @internal
      * @example
      *
      *     class Example {
      *         // property declarations are not part of ES6, though they are valid in TypeScript:
      *         // static staticProperty;
      *         // property;
      *
      *         static staticMethod(p) { }
      *         method(p) { }
      *     }
      *
      *     // property (on constructor)
      *     defineMetadata("custom:annotation", Number, Example, "staticProperty");
      *
      *     // property (on prototype)
      *     defineMetadata("custom:annotation", Number, Example.prototype, "property");
      *
      *     // method (on constructor)
      *     defineMetadata("custom:annotation", Number, Example, "staticMethod");
      *
      *     // method (on prototype)
      *     defineMetadata("custom:annotation", Number, Example.prototype, "method");
      *
      *     // decorator factory as metadata-producing annotation.
      *     function MyAnnotation(options): PropertyDecorator {
      *         return (target, key) => defineMetadata("custom:annotation", options, target, key);
      *     }
      *
      */
    Container.prototype.defineMetadata = function (metadataKey, metadataValue, target, propertyKey) {
        var metadataMap = this._getOrCreateMetadataMap(target, propertyKey, /*Create*/ true);
        metadataMap.set(metadataKey, metadataValue);
    };
    /**
      * Gets the metadata value for the provided metadata key on the target object.
      * @param metadataKey A key used to store and retrieve metadata.
      * @param target The target object on which the metadata is defined.
      * @param propertyKey The property key for the target.
      * @returns The metadata value for the metadata key if found; otherwise, `undefined`.
      * @internal
      * @example
      *
      *     class Example {
      *         // property declarations are not part of ES6, though they are valid in TypeScript:
      *         // static staticProperty;
      *         // property;
      *
      *         static staticMethod(p) { }
      *         method(p) { }
      *     }
      *
      *     // property (on constructor)
      *     result = getOwnMetadata("custom:annotation", Example, "staticProperty");
      *
      *     // property (on prototype)
      *     result = getOwnMetadata("custom:annotation", Example.prototype, "property");
      *
      *     // method (on constructor)
      *     result = getOwnMetadata("custom:annotation", Example, "staticMethod");
      *
      *     // method (on prototype)
      *     result = getOwnMetadata("custom:annotation", Example.prototype, "method");
      *
      */
    Container.prototype.getOwnMetadata = function (metadataKey, target, propertyKey) {
        var metadataMap = this._getOrCreateMetadataMap(target, propertyKey, /*Create*/ false);
        if (metadataMap === undefined)
            return undefined;
        return metadataMap.get(metadataKey);
    };
    return Container;
}());
export { Container };
//# sourceMappingURL=data:application/json;base64,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